import { PRIMARY_BUTTON, SECONDARY_BUTTON } from "@/constants";
import InputWithAddOn from "@/app/components/ui/Inputs/InputWithAddOn";
import NoDataFoundComponent from "@/app/components/ui/NoRecordFound";
import GenericBulkUploadModal from "@/app/components/ui/Modals/GenericBulkUploadModal";
import { useBulkUpdateDiscountAPI } from "@/app/hooks/api_hooks/Inventory/useBulkUpdateDiscountAPI";
import { useBulkUpdateBusinessScoreAPI } from "@/app/hooks/api_hooks/Inventory/useBulkUpdateBusinessScoreAPI";
import { useBulkUpdatePersonaDiscountAPI } from "@/app/hooks/api_hooks/Inventory/useBulkUpdatePersonaDiscountAPI";
import { useDownloadCatalogAPI } from "@/app/hooks/api_hooks/Inventory/useDownloadCatalogAPI";
import PaginationComponent from "@/app/components/ui/Pagination";
import SearchableSelectMenu from "@/app/components/ui/SelectMenus/SearchableSelectMenu";
import TableSkeleton from "@/app/components/ui/Skeletons/TableSkeleton";
import Toggle from "@/app/components/ui/Toggles";
import { useInventoryProductListAP } from "@/app/hooks/api_hooks/Inventory/useInventoryProductListAP";
import { useProductStatusListAPI } from "@/app/hooks/api_hooks/Inventory/useProductStatusListAPI";
import { useImageConstants } from "@/app/hooks/useImageConstants";
import { useLoader } from "@/app/hooks/useLoader";
import useRouteInfo from "@/app/hooks/useRouteInfo";
import { useRouter } from "@/app/hooks/useRouter";
import { useSelectMenuReducer } from "@/app/hooks/useSelectMenuReducer";
import { useAppDispatch } from "@/app_redux/hooks/root_hook";
import { IInventoryState, setProductCopyDetails } from "@/app_redux/reducers/slices/auth/inventory_slice";
import { useEffect, useState } from "react";
import { useForm } from "react-hook-form";
import { FaProductHunt } from "react-icons/fa6";
import { FiEdit, FiDownload } from "react-icons/fi";
import { useNavigate } from "react-router";
import clsx from "clsx";

export interface IInventoryProductFilter {
  status: string;
  statusId: string;
}

const PAGE_SIZE = 30;

const tableHeader = [
  { item: "SN" },
  { item: "Product Name" },
  { item: "Product Status" },
  { item: "Visible on VMS" },
  { item: "Visible on App" },
  { item: "Persona Discount" },
  { item: "Action" },
];

const ProductListComponent = () => {
  const { NO_PWL_RESULTS } = useImageConstants();

  const { getRouteKey } = useRouter();
  const routeId = getRouteKey("INVENTORY", "id");
  const { routeState } = useRouteInfo(routeId);
  const { productList } = routeState.state as IInventoryState;
  const [currentPage, setCurrentPage] = useState<number>(1);
  const [searchQuery, setSearchQuery] = useState<string>("");
  const { execute: fetchproductList } = useInventoryProductListAP();
  const { execute: fetchStatusDropdownList, productOrderStatus } = useProductStatusListAPI();
  const [selectedStatusId, setSelectedStatusId] = useState<string>("");
  const isProductListInProgess = useLoader("isInventoryProductListLoadingInProgess");
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const [isBulkDiscountModalOpen, setIsBulkDiscountModalOpen] = useState<boolean>(false);
  const { execute: uploadBulkDiscount } = useBulkUpdateDiscountAPI();
  const [isBulkBusinessScoreModalOpen, setIsBulkBusinessScoreModalOpen] = useState<boolean>(false);
  const { execute: uploadBulkBusinessScore } = useBulkUpdateBusinessScoreAPI();
  const [isBulkPersonaDiscountModalOpen, setIsBulkPersonaDiscountModalOpen] = useState<boolean>(false);
  const { execute: uploadBulkPersonaDiscount } = useBulkUpdatePersonaDiscountAPI();
  const { execute: downloadCatalog, isLoading: isCatalogDownloading } = useDownloadCatalogAPI();

  const formHook = useForm<IInventoryProductFilter>({
    mode: "onChange",
    defaultValues: {},
  });

  useEffect(() => {
    fetchStatusDropdownList();
  }, []);

  useEffect(() => {
    const encodedQuery = searchQuery.replace(/\+/g, "%2B");
    if (currentPage) {
      fetchproductList("0", PAGE_SIZE, currentPage, encodedQuery);
      // setCurrentPage(1);
    }
  }, [currentPage, searchQuery]);

  const statusList = useSelectMenuReducer(productOrderStatus, "status", "id", {
    withAllSelectOption: true,
    allSelectItemOptions: {
      title: "All",
    },
  });

  const handelProductEdit = (id: string) => {
    navigate(`/app/inventory/product-add?product_id=${id}`);
  };
  const handelCreateProduct = () => {
    navigate(`/app/inventory/product-add`);
  };

  useEffect(() => {
    const status_id = formHook.getValues("statusId") || selectedStatusId;
    const encodedQuery = searchQuery.replace(/\+/g, "%2B");
    if (status_id) {
      fetchproductList(status_id === "-1" ? "0" : status_id, PAGE_SIZE, 1, encodedQuery);
      // setCurrentPage(1);
    }
  }, [selectedStatusId, searchQuery]);

  useEffect(() => {
    dispatch(setProductCopyDetails({ data: null }));
  }, []);

  return (
    <>
      <div className="bg-white sm:px-6 lg:px-8 p-4 h-full">
        <div className="sm:flex-auto">
          <h1 className="font-semibold text-sm sm:text-base text-gray-900 leading-6">Listed Products</h1>
          <p className="mt-2 text-gray-700 text-sm hidden sm:block">A list of all the products for the inventories.</p>
        </div>
        <div className="flex flex-col gap-4 lg:flex-row lg:justify-between lg:items-center">
          <div className="mt-4 w-full lg:w-1/6">
            <SearchableSelectMenu
              errorMessages={[]}
              onSelectItem={(item) => {
                if (item) {
                  formHook.setValue("status", item.title);
                  formHook.setValue("statusId", item.id);
                  setSelectedStatusId(item.id);
                  setCurrentPage(1);
                }
              }}
              fieldError={formHook.formState.errors.status}
              register={formHook.register("status", { required: false })}
              selectItems={statusList}
              placeholder="Select Status"
              showTooltips={true}
              showTypedErrors={true}
              showDropdownIcon={true}
              defaultSelected={statusList?.filter((oc) => oc.title === formHook.watch("status"))[0]}
              listBoxClassName="w-full"
              className="text-gray-800"
              containerClassName="w-full"
            />
          </div>
          <div className="flex flex-col sm:flex-row sm:flex-wrap gap-2 sm:gap-3 sm:items-center mt-0 lg:mt-4">
            <button
              type="button"
              className={clsx(
                SECONDARY_BUTTON,
                "w-full sm:w-auto min-w-[155px] flex items-center justify-center gap-2",
                isCatalogDownloading && "cursor-not-allowed opacity-80"
              )}
              onClick={() => downloadCatalog()}
              disabled={isCatalogDownloading}
            >
              {isCatalogDownloading ? (
                <>
                  <span className="animate-spin inline-block w-3.5 h-3.5 border-[2px] border-current border-t-transparent rounded-full flex-shrink-0" />
                  <span>Downloading...</span>
                </>
              ) : (
                <>
                  <FiDownload className="w-3.5 h-3.5 flex-shrink-0" />
                  <span>Download Catalog</span>
                </>
              )}
            </button>
            <button
              type="button"
              className={clsx(SECONDARY_BUTTON, "w-full sm:w-auto")}
              onClick={() => setIsBulkDiscountModalOpen(true)}
            >
              Bulk Update Discount
            </button>
            <button
              type="button"
              className={clsx(SECONDARY_BUTTON, "w-full sm:w-auto")}
              onClick={() => setIsBulkBusinessScoreModalOpen(true)}
            >
              Bulk Update Business Score
            </button>
            <button
              type="button"
              className={clsx(SECONDARY_BUTTON, "w-full sm:w-auto")}
              onClick={() => setIsBulkPersonaDiscountModalOpen(true)}
            >
              Bulk Persona Discount
            </button>
            <button
              type="button"
              className={clsx(PRIMARY_BUTTON, "w-full sm:w-auto")}
              onClick={() => handelCreateProduct()}
            >
              + Add New Product
            </button>
          </div>
        </div>
      </div>
      <div className="bg-white px-4 sm:px-6 lg:px-8">
        <div className="hidden sm:flex">
          <InputWithAddOn
            onEnterKeyPress={(query: string) => setSearchQuery(query)}
            containerClassName="w-full sm:w-1/2 lg:w-1/3"
            addon={<FaProductHunt className="w-4 h-4" />}
            placeholder="Search Product"
          />
        </div>
        <input
          type="text"
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
          className="h-[38px] w-full rounded-lg border border-gray-300 px-3 text-xs sm:hidden mt-2"
          placeholder="Search Product"
        />
        <div className="mt-8 flow-root">
          {isProductListInProgess ? (
            <TableSkeleton />
          ) : productList?.data.length === 0 ? (
            <NoDataFoundComponent
              imageURL={NO_PWL_RESULTS}
              message="No Product Listed"
              description="No Product Listed, please onboard some products."
            />
          ) : (
            <>
              <div className="outer-container relative rounded-xl bg-[#F9FAFB]">
                <div className="scroll-inner !h-auto max-h-[600px]">
                  <div className="w-full">
                    <div className="inline-block min-w-full align-middle">
                      <div className="bg-white rounded-lg">
                        <table className="w-full min-w-[640px] border-separate border-spacing-x-0 border-spacing-y-0.5 bg-[#F5F5F5] rounded-xl px-[3px] pb-[1px]">
                          <thead className="bg-[#F5F5F5] text-[#1F1F23] top-0 font-normal">
                            <tr>
                              {tableHeader?.map((key, index) => (
                                <th
                                  key={index}
                                  scope="col"
                                  className="px-3 py-3 text-left whitespace-nowrap text-xs font-medium text-[#1F1F23] capitalize"
                                >
                                  {key?.item}
                                </th>
                              ))}
                            </tr>
                          </thead>
                          <tbody className="overflow-hidden rounded-xl w-full">
                            {productList?.data?.map((product, idx) => {
                              const isFirst = idx === 0;
                              const isLast = idx === productList?.data?.length - 1;
                              const isSingle = productList?.data?.length === 1;

                              return (
                                <tr
                                  key={product?.vetic_variant_id}
                                  className={clsx(
                                    "group bg-white transition",
                                    isSingle && "rounded-xl",
                                    isFirst && !isSingle && "rounded-t-xl",
                                    isLast && !isSingle && "rounded-b-xl",
                                  )}
                                >
                                  <td
                                    className={clsx(
                                      "px-3 py-3 whitespace-nowrap text-xs font-normal",
                                      isSingle && "rounded-tl-2xl rounded-bl-2xl",
                                      isFirst && !isSingle && "rounded-tl-2xl",
                                      isLast && !isSingle && "rounded-bl-2xl",
                                    )}
                                  >
                                    {(currentPage - 1) * PAGE_SIZE + (idx + 1)}
                                  </td>
                                  <td className="px-3 py-3 text-xs font-normal">{product?.product_name}</td>
                                  <td className="px-3 py-3 whitespace-nowrap text-xs font-normal text-[#6C727F]">
                                    {product?.product_status}
                                  </td>
                                  <td className="px-3 py-3 whitespace-nowrap text-xs font-normal text-[#6C727F]">
                                    {product?.visible_on_vms ? "Yes" : "No"}
                                  </td>
                                  <td className="px-3 py-3 whitespace-nowrap text-xs font-normal text-[#6C727F]">
                                    {product?.visible_on_app ? "Yes" : "No"}
                                  </td>
                                  <td className="px-3 py-3 whitespace-nowrap text-xs font-normal">
                                    <button
                                      className="text-gray-500 hover:text-black cursor-pointer"
                                      onClick={() =>
                                        navigate(
                                          `/app/inventory/product-persona-discount?product_id=${product?.vetic_variant_id}`,
                                        )
                                      }
                                      title="Add Persona Discount"
                                      aria-label="Add Persona Discount"
                                    >
                                      <FiEdit className="w-4 h-4" />
                                    </button>
                                  </td>
                                  <td
                                    className={clsx(
                                      "px-3 py-3 whitespace-nowrap text-xs font-normal",
                                      isSingle && "rounded-tr-2xl rounded-br-2xl",
                                      isFirst && !isSingle && "rounded-tr-2xl",
                                      isLast && !isSingle && "rounded-br-2xl",
                                    )}
                                  >
                                    <button
                                      className="text-gray-500 hover:text-black cursor-pointer"
                                      onClick={() => handelProductEdit(product?.vetic_variant_id)}
                                      title="Edit"
                                      aria-label="Edit"
                                    >
                                      <FiEdit className="w-4 h-4" />
                                    </button>
                                  </td>
                                </tr>
                              );
                            })}
                          </tbody>
                        </table>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
              <div className="flex justify-center items-center p-4 w-full overflow-x-auto">
                <PaginationComponent
                  className="pagination-bar"
                  currentPage={currentPage}
                  pageSize={PAGE_SIZE}
                  totalCount={productList?.total_items}
                  onPageChange={(pageNumber) => setCurrentPage(pageNumber)}
                />
              </div>
            </>
          )}
        </div>
      </div>
      <GenericBulkUploadModal
        open={isBulkDiscountModalOpen}
        onClose={() => setIsBulkDiscountModalOpen(false)}
        title="Bulk Update Discount"
        sampleDownloadUrl="/bulk_update_discount.xlsx"
        sampleDownloadLabel="Download Sample Excel"
        onUpload={(file) => uploadBulkDiscount(file)}
        onSuccess={() => {}}
      />
      <GenericBulkUploadModal
        open={isBulkBusinessScoreModalOpen}
        onClose={() => setIsBulkBusinessScoreModalOpen(false)}
        title="Bulk Business Score Update"
        sampleDownloadUrl="/bulk_business_score.xlsx"
        sampleDownloadLabel="Download Sample Excel"
        onUpload={(file) => uploadBulkBusinessScore(file)}
        onSuccess={() => {}}
      />
      <GenericBulkUploadModal
        open={isBulkPersonaDiscountModalOpen}
        onClose={() => setIsBulkPersonaDiscountModalOpen(false)}
        title="Bulk Persona Discount Update"
        sampleDownloadUrl="/bulk_update_persona_discount.csv"
        sampleDownloadLabel="Download Sample CSV"
        onUpload={(file) => uploadBulkPersonaDiscount(file)}
        onSuccess={() => {}}
      />
    </>
  );
};

export default ProductListComponent;
